<script setup lang="ts">
import { faMinus, faPlus } from '@fortawesome/pro-regular-svg-icons';

interface IProps {
    expanded: boolean;
    faq: { title?: string; value?: string };
    highlight?: string;
}
const { faq, expanded, highlight } = defineProps<IProps>();

const emit = defineEmits<{
    toggle: [];
}>();

const maxHeight = ref(0);

const content = ref<HTMLDivElement>();

onMounted(() =>
    nextTick(() => {
        maxHeight.value = content.value?.clientHeight || 0;
    })
);

const title = computed(() => {
    if (!highlight) {
        return faq.title;
    }
    return faq.title?.replaceAll(
        highlight,
        `<span class="highlight">${highlight}</span>`
    );
});
</script>

<template>
    <div>
        <button
            class="accordion"
            :class="{ active: expanded }"
            @click="emit('toggle')"
        >
            <div class="title" v-html="title" />
            <mr-icon
                :icon="expanded ? faMinus : faPlus"
                color="var(--blue-400)"
            />
        </button>
        <div
            class="panel"
            :class="{ expanded }"
            :style="{
                maxHeight: expanded ? `${maxHeight}px` : undefined,
            }"
        >
            <div ref="content" class="content" v-html="faq.value" />
        </div>
        <slot name="separator" />
    </div>
</template>
<style scoped lang="scss">
.accordion :deep(.index) {
    display: none;
}

.accordion {
    all: unset;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: none;
    cursor: pointer;
    padding: 19px 0;
    width: 100%;
    border: none;
    outline: none;
    transition: 0.4s;
    font-style: normal;
    @include font-weight(bold);
    font-size: 14px;
    line-height: 22px;
    text-align: right;
    color: usePalette($p-cool-gray, $p-800);
    @include set-font();
    position: relative;
}

.title {
    :deep(.highlight) {
        color: usePalette();
    }
}

.panel {
    transition: max-height 250ms 10ms ease-out;
    overflow: hidden;
    text-align: justify;
    margin: 0;
    font-size: 14px;
    font-style: normal;
    @include font-weight();
    color: usePalette($p-cool-gray, $p-800);
    line-height: 22px;
    max-height: 0;
    &.expanded {
        transition: max-height 250ms ease-in;
    }
    .content {
        padding-bottom: 22px;
    }
}

@media (max-width: $app-mobile-max-width) {
    .accordion :deep(div) {
        margin-left: 35px;
    }
}
</style>
